<template>
  <div class="image-preview-container">
    <FormLabel :label="label" />
    <div class="swiper-wrapper-container">
      <swiper class="swiper" :options="swiperOption" ref="imageSwiper">
        <template v-if="displayImages.length > 0">
          <swiper-slide
            v-for="(img, index) in displayImages"
            :key="img.src || index"
          >
            <!-- PDFはiframeで表示（vue-pdfはXHRで取得するため別オリジンでCORSエラーになる） -->
            <iframe
              v-if="img.isPdf && img.src"
              :src="img.src"
              class="preview-pdf-iframe"
              title="PDF"
            />
            <img v-else-if="img.src" :src="img.src" :alt="img.alt" />
          </swiper-slide>
        </template>
        <swiper-slide v-else>
          <div class="swiper-empty">表示できる画像・PDFがありません</div>
        </swiper-slide>
      </swiper>
      <!-- カスタムナビゲーションボタン -->
      <div
        class="custom-nav-prev"
        :class="{ disabled: isBeginning }"
        @click="slidePrev"
      >
        <feather-icon icon="ChevronLeftIcon" svgClasses="w-8 h-8" />
      </div>
      <div
        class="custom-nav-next"
        :class="{ disabled: isEnd }"
        @click="slideNext"
      >
        <feather-icon icon="ChevronRightIcon" svgClasses="w-8 h-8" />
      </div>
    </div>
  </div>
</template>

<script>
import FormLabel from "./form/FormLabel.vue";
import { swiper, swiperSlide } from "vue-awesome-swiper";
import "swiper/dist/css/swiper.css";

export default {
  name: "Imagepreview",
  components: {
    FormLabel,
    swiper,
    swiperSlide,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * 画像データ配列（v-model用）
     * @type {Array}
     * @default []
     */
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isBeginning: true,
      isEnd: false,
      swiperOption: {
        // 1枚ずつ表示する設定
        slidesPerView: 1,
        observer: true,
        observeParents: true,
        // ページネーション
        pagination: {
          // el: ".swiper-pagination",
        },
        // カスタムナビゲーションを使用するため、デフォルトのナビゲーションは無効化
        on: {
          slideChange: () => {
            this.updateNavigationState();
          },
          init: () => {
            this.$nextTick(() => {
              this.updateNavigationState();
            });
          },
        },
      },
    };
  },
  computed: {
    /**
     * 表示用画像・PDFデータ
     */
    displayImages() {
      if (this.value && this.value.length > 0) {
        return this.value.map((img, index) => {
          const src =
            typeof img === "string"
              ? img
              : img.previewUrl || img.src || img.url || img;
          const name = typeof img === "string" ? "" : img.name || img.alt || "";
          const isPdf =
            (typeof img === "string" && img.toLowerCase().includes(".pdf")) ||
            (name && name.toLowerCase().endsWith(".pdf")) ||
            (src && String(src).toLowerCase().includes(".pdf"));
          return {
            src,
            alt: name || `Image ${index + 1}`,
            isPdf,
          };
        });
      }
      return [];
    },
  },
  watch: {
    displayImages: {
      handler() {
        // 画像データが変更されたらSwiperを更新
        this.$nextTick(() => {
          const swiper =
            this.$refs.imageSwiper && this.$refs.imageSwiper.swiper;
          if (swiper) {
            swiper.update();
            this.updateNavigationState();
          }
        });
      },
      deep: true,
    },
  },
  methods: {
    /**
     * ナビゲーションボタンの状態を更新
     */
    updateNavigationState() {
      const swiper = this.$refs.imageSwiper && this.$refs.imageSwiper.swiper;
      if (swiper) {
        this.isBeginning = swiper.isBeginning;
        this.isEnd = swiper.isEnd;
      }
    },
    /**
     * 前のスライドへ移動
     */
    slidePrev() {
      const swiper = this.$refs.imageSwiper && this.$refs.imageSwiper.swiper;
      if (swiper && !this.isBeginning) {
        swiper.slidePrev();
      }
    },
    /**
     * 次のスライドへ移動
     */
    slideNext() {
      const swiper = this.$refs.imageSwiper && this.$refs.imageSwiper.swiper;
      if (swiper && !this.isEnd) {
        swiper.slideNext();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

// ============================================
// メインコンテナ
// ============================================
.image-preview-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  overflow: hidden;
}

// ============================================
// Swiperラッパー
// ============================================
.swiper-wrapper-container {
  width: 100%;
  position: relative;
}

.swiper {
  width: 100%;
  height: 280px;
  background-color: $aisea-white;
  border-radius: 6px;
  overflow: hidden;
  box-sizing: border-box;
}

// ============================================
// スライド
// ============================================
.swiper-slide {
  width: 100% !important;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.swiper-slide {
  padding: 8px 48px;
}

.swiper-slide img {
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
  margin: 8px, 64px;
}

.swiper-slide .preview-pdf-iframe {
  width: 100%;
  height: 100%;
  min-height: 260px;
  border: none;
  border-radius: 6px;
}

.swiper-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #626262;
  font-size: 14px;
  padding: 0 24px;
}

// ============================================
// カスタムナビゲーションボタン
// ============================================
.custom-nav-prev,
.custom-nav-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background-color: $aisea-white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;

  &:hover {
    opacity: 0.8;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;

    &:hover {
      opacity: 0.3;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.custom-nav-prev {
  left: 16px;
}

.custom-nav-next {
  right: 16px;
}
</style>
